import { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { useTheme } from '@/contexts/ThemeContext';
import type { SimilarLessonItem } from '@/mocks/lessons';
import { userPlan } from '@/mocks/lessons';
import Pressable from '@/components/base/Pressable';

const similarPreviews: Record<string, string> = {
  'sim-001': 'https://readdy.ai/api/search-image?query=minimal-monochrome-black-and-white-educational-illustration-calculus-trigonometric-wave-graph-sine-cosine-curves-on-clean-white-background-simple-geometric-elegant-academic-style-flat-2d-vector-aesthetic-premium-design&width=240&height=280&seq=sim-001&orientation=portrait',
  'sim-002': 'https://readdy.ai/api/search-image?query=minimal-monochrome-black-and-white-educational-illustration-calculus-table-grid-matrix-systematic-rows-columns-on-clean-white-background-simple-geometric-elegant-academic-style-flat-2d-vector-aesthetic-premium-design&width=240&height=280&seq=sim-002&orientation=portrait',
  'sim-003': 'https://readdy.ai/api/search-image?query=minimal-monochrome-black-and-white-educational-illustration-calculus-definite-integral-bounded-area-under-curve-shaded-region-on-clean-white-background-simple-geometric-elegant-academic-style-flat-2d-vector-aesthetic-premium-design&width=240&height=280&seq=sim-003&orientation=portrait',
};

const lockedPreviews: Record<string, string> = {
  'sim-002': 'https://readdy.ai/api/search-image?query=minimal-monochrome-black-and-white-educational-illustration-calculus-table-grid-locked-pattern-systematic-rows-columns-on-clean-white-background-simple-geometric-elegant-academic-style-flat-2d-vector-aesthetic-premium-design&width=240&height=280&seq=sim-002-locked&orientation=portrait',
  'sim-003': 'https://readdy.ai/api/search-image?query=minimal-monochrome-black-and-white-educational-illustration-calculus-integral-bounded-area-locked-shaded-region-on-clean-white-background-simple-geometric-elegant-academic-style-flat-2d-vector-aesthetic-premium-design&width=240&height=280&seq=sim-003-locked&orientation=portrait',
};

interface GenerateSimilarProps {
  lessons: SimilarLessonItem[];
}

export default function GenerateSimilar({ lessons }: GenerateSimilarProps) {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';
  const [generatedCount, setGeneratedCount] = useState(
    lessons.filter((l) => l.generated).length
  );
  const [showPremium, setShowPremium] = useState(false);

  const generated = lessons.filter((l) => l.generated);
  const locked = lessons.filter((l) => !l.generated);
  const isFree = userPlan.tier === 'free';
  const canGenerate = !isFree || generatedCount < userPlan.similarLessonsMax;

  const handleGenerate = () => {
    if (!canGenerate) {
      setShowPremium(true);
      return;
    }
    const next = locked[0];
    if (next) {
      setGeneratedCount((c) => c + 1);
    }
  };

  const t = {
    textPrimary: isDark ? 'text-white' : 'text-neutral-900',
    textMuted: isDark ? 'text-white/30' : 'text-black/30',
    textFaint: isDark ? 'text-white/25' : 'text-black/25',
    textSuccess: isDark ? 'text-emerald-400' : 'text-emerald-600',
    textSuccessMuted: isDark ? 'text-emerald-400/60' : 'text-emerald-600/60',
    textLocked: isDark ? 'text-white/20' : 'text-black/20',
    iconBg: isDark ? 'bg-white/[0.06]' : 'bg-black/[0.04]',
    iconMuted: isDark ? 'text-white/40' : 'text-black/40',
    cardBg: isDark ? 'bg-[#111111]' : 'bg-white',
    cardBorder: isDark ? 'border-white/[0.05]' : 'border-black/[0.05]',
    cardBorderHover: isDark ? 'hover:border-white/[0.10]' : 'hover:border-black/[0.10]',
    lockedCardBg: isDark ? 'bg-white/[0.015]' : 'bg-black/[0.015]',
    lockedCardBorder: isDark ? 'border-white/[0.03]' : 'border-black/[0.03]',
    lockedText: isDark ? 'text-white/50' : 'text-black/50',
    lockedDesc: isDark ? 'text-white/30' : 'text-black/30',
    lockedSub: isDark ? 'text-white/20' : 'text-black/20',
    subjectTag: isDark ? 'bg-white/[0.06] text-white/50' : 'bg-black/[0.04] text-black/50',
    lockedTag: isDark ? 'bg-white/[0.04] text-white/30' : 'bg-black/[0.04] text-black/30',
    gradientLeft: isDark ? 'bg-gradient-to-l from-transparent via-transparent to-[#111111]' : 'bg-gradient-to-l from-transparent via-transparent to-white',
    lockedGradient: isDark ? 'bg-gradient-to-l from-transparent via-transparent to-white/[0.015]' : 'bg-gradient-to-l from-transparent via-transparent to-black/[0.015]',
    btnPrimary: isDark ? 'bg-white text-black' : 'bg-black text-white',
    btnInactive: isDark ? 'bg-white/[0.04] text-white/30 border-white/[0.06]' : 'bg-black/[0.04] text-black/30 border-black/[0.06]',
    premiumBg: isDark ? 'bg-amber-500/8 border-amber-500/12' : 'bg-amber-50 border-amber-200/50',
    premiumIcon: isDark ? 'text-amber-400' : 'text-amber-600',
    premiumText: isDark ? 'text-amber-400' : 'text-amber-600',
    premiumSub: isDark ? 'text-white/35' : 'text-black/35',
    premiumBtn: isDark ? 'bg-amber-500/10 text-amber-400 border-amber-500/15' : 'bg-amber-50 text-amber-600 border-amber-200/50',
    lockedOverlay: isDark ? 'bg-black/60' : 'bg-white/60',
    lockIconBg: isDark ? 'bg-white/[0.08]' : 'bg-black/[0.08]',
    lockIcon: isDark ? 'text-white/30' : 'text-black/30',
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, ease: [0.32, 0.72, 0, 1] }}
      className="px-5 pb-4"
    >
      {/* Section Header */}
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-2xl ${t.iconBg} flex items-center justify-center`}>
            <div className="w-5 h-5 flex items-center justify-center">
              <i className={`ri-magic-line ${t.iconMuted} text-sm`} />
            </div>
          </div>
          <div>
            <h3 className={`${t.textPrimary} text-sm font-semibold`}>Similar Lessons</h3>
            <p className={`${t.textFaint} text-[11px]`}>
              {isFree
                ? `${Math.max(0, userPlan.similarLessonsMax - generatedCount)} free generation${userPlan.similarLessonsMax - generatedCount !== 1 ? 's' : ''} remaining`
                : 'Unlimited with Premium'}
            </p>
          </div>
        </div>
      </div>

      {/* Generated lessons */}
      {generated.length > 0 && (
        <div className="space-y-3 mb-5">
          {generated.map((lesson) => (
            <motion.div
              key={lesson.id}
              initial={{ opacity: 0, y: 10, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ type: 'spring', stiffness: 380, damping: 28 }}
            >
              <Pressable
                onClick={() => {}}
                className="w-full text-left group"
                scale={0.97}
              >
                <div className={`relative rounded-3xl border ${t.cardBorder} ${t.cardBg} overflow-hidden transition-all duration-500 ${t.cardBorderHover}`}>
                  <div className="flex">
                    {/* Text side */}
                    <div className="flex-1 p-5 min-w-0">
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-full ${t.subjectTag} text-[10px] font-semibold tracking-wide mb-3`}>
                        {lesson.subject}
                      </span>
                      <h4 className={`${t.textPrimary} text-sm font-semibold leading-snug mb-2.5`}>
                        {lesson.title}
                      </h4>
                      <p className={`${t.textMuted} text-[12px] leading-relaxed line-clamp-2`}>
                        {lesson.description}
                      </p>
                      <div className="flex items-center gap-1.5 mt-3">
                        <div className="w-3.5 h-3.5 flex items-center justify-center">
                          <i className={`ri-check-line ${t.textSuccess} text-[10px]`} />
                        </div>
                        <span className={`${t.textSuccessMuted} text-[10px] font-medium`}>Generated</span>
                      </div>
                    </div>

                    {/* Preview side */}
                    <div className="w-[100px] md:w-[110px] flex-shrink-0 relative overflow-hidden rounded-r-3xl">
                      <img
                        src={similarPreviews[lesson.id] || similarPreviews['sim-001']}
                        alt={lesson.title}
                        className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-110"
                        loading="lazy"
                      />
                      <div className={`absolute inset-0 ${t.gradientLeft}`} />
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="w-8 h-8 rounded-full bg-white/[0.15] backdrop-blur-sm flex items-center justify-center">
                          <div className="w-3.5 h-3.5 flex items-center justify-center">
                            <i className="ri-play-fill text-white text-[10px]" />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </Pressable>
            </motion.div>
          ))}
        </div>
      )}

      {/* Locked lessons */}
      <div className="space-y-3 mb-5">
        {locked.slice(0, 2).map((lesson, i) => (
          <motion.div
            key={lesson.id}
            initial={{ opacity: 0, y: 10, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ delay: i * 0.05, type: 'spring', stiffness: 380, damping: 28 }}
          >
            <div className={`relative rounded-3xl border ${t.lockedCardBorder} ${t.lockedCardBg} overflow-hidden opacity-55`}>
              <div className="flex">
                <div className="flex-1 p-5 min-w-0">
                  <span className={`inline-flex items-center px-2.5 py-1 rounded-full ${t.lockedTag} text-[10px] font-semibold tracking-wide mb-3`}>
                    {lesson.subject}
                  </span>
                  <h4 className={`${t.lockedText} text-sm font-semibold leading-snug mb-2.5`}>
                    {lesson.title}
                  </h4>
                  <p className={`${t.lockedDesc} text-[12px] leading-relaxed line-clamp-2`}>
                    {lesson.description}
                  </p>
                  <div className="flex items-center gap-1.5 mt-3">
                    <div className="w-3.5 h-3.5 flex items-center justify-center">
                      <i className={`ri-lock-line ${t.lockedSub} text-[10px]`} />
                    </div>
                    <span className={`${t.lockedSub} text-[10px] font-medium`}>Locked</span>
                  </div>
                </div>
                <div className="w-[100px] md:w-[110px] flex-shrink-0 relative overflow-hidden rounded-r-3xl">
                  <img
                    src={lockedPreviews[lesson.id] || lockedPreviews['sim-002']}
                    alt={lesson.title}
                    className="w-full h-full object-cover grayscale"
                    loading="lazy"
                  />
                  <div className={`absolute inset-0 ${t.lockedGradient}`} />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className={`w-8 h-8 rounded-full ${t.lockIconBg} flex items-center justify-center`}>
                      <div className="w-3.5 h-3.5 flex items-center justify-center">
                        <i className={`ri-lock-line ${t.lockIcon} text-[10px]`} />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Generate button */}
      <motion.button
        onClick={handleGenerate}
        whileTap={{ scale: 0.95 }}
        disabled={!canGenerate && locked.length === 0}
        className={`w-full py-3.5 rounded-2xl text-xs font-semibold flex items-center justify-center gap-2 cursor-pointer transition-all duration-200 ${
          canGenerate && locked.length > 0 ? t.btnPrimary : t.btnInactive
        }`}
      >
        <div className="w-4 h-4 flex items-center justify-center">
          <i className={`${canGenerate ? 'ri-sparkling-line' : 'ri-lock-line'} text-xs`} />
        </div>
        {canGenerate && locked.length > 0
          ? 'Generate Similar Lesson'
          : isFree
            ? 'Upgrade for More Generations'
            : 'All similar lessons generated'}
      </motion.button>

      {/* Premium CTA */}
      <AnimatePresence>
        {showPremium && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            className={`mt-3 p-3.5 rounded-2xl ${t.premiumBg} border`}
          >
            <div className="flex items-center gap-2 mb-2">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className={`ri-vip-crown-line ${t.premiumIcon} text-xs`} />
              </div>
              <span className={`${t.premiumText} text-xs font-semibold`}>Free Plan Limit Reached</span>
            </div>
            <p className={`${t.premiumSub} text-[11px] leading-relaxed mb-2.5`}>
              Upgrade to Premium for unlimited similar lesson generations, faster AI processing, and premium narration voices.
            </p>
            <motion.button
              whileTap={{ scale: 0.95 }}
              className={`w-full ${t.premiumBtn} py-2.5 rounded-xl text-[11px] font-semibold border cursor-pointer`}
            >
              View Premium Plans
            </motion.button>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}